import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Ban, Wallet, TrendingUp, Flag, Trash2, ShieldOff, Check, UserX } from "lucide-react";
import { Button } from "../components/ui/button";

const REASON_STYLE = "bg-[#FBE9E9] text-[#B23B3B]";

export default function Admin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loadingReports, setLoadingReports] = useState(true);
  const [busyId, setBusyId] = useState("");

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      const { data } = await api.get("/admin/reports", { params: filter === "all" ? {} : { status: filter } });
      setReports(data);
    } finally { setLoadingReports(false); }
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/earnings").then((r) => setData(r.data)).catch(() => setErr(true));
  }, [user]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    loadReports();
    // eslint-disable-next-line
  }, [user, filter]);

  const removeContent = async (r) => {
    setBusyId(r.id);
    try {
      await api.delete(`/admin/content/${r.target_type}/${r.target_id}`);
      toast.success("Content removed");
      await loadReports();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  const banUser = async (r) => {
    if (!r.target_owner_id) return toast.error("No user linked to this report");
    setBusyId(r.id);
    try {
      await api.post(`/admin/users/${r.target_owner_id}/ban`);
      toast.success(`${r.target_owner_alias || "User"} banned`);
      await loadReports();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  const unbanUser = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/users/${r.target_owner_id}/unban`);
      toast.success("User reinstated");
      await loadReports();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  const dismiss = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/reports/${r.id}/resolve`);
      toast.success("Report dismissed");
      await loadReports();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  if (user && user.role !== "admin") return <Navigate to="/feed" replace />;
  if (err) return <div className="text-center py-20 text-[#6B7280]">Unable to load admin data.</div>;
  if (!data) return <div className="flex justify-center py-20 text-[#6B7280]"><Loader2 className="animate-spin" /></div>;

  const total = (data.total_cancellation_fees + data.total_errand_payments).toFixed(2);
  const stats = [
    { label: "Cancellation fees", value: `$${data.total_cancellation_fees.toFixed(2)}`, sub: `${data.cancellation_fee_count} charged`, icon: Ban, color: "#B23B3B", bg: "#FBF3F3" },
    { label: "Errand payments", value: `$${data.total_errand_payments.toFixed(2)}`, sub: `${data.errand_payment_count} paid`, icon: Wallet, color: "#2F8F6E", bg: "#E7F6F0" },
    { label: "Total collected", value: `$${total}`, sub: "all-time", icon: TrendingUp, color: "#FF7A59", bg: "#FFF1EC" },
  ];
  const openCount = reports.filter((r) => r.status === "open").length;

  return (
    <div className="max-w-4xl mx-auto px-5 py-8">
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1A1D20] mb-1">Admin</h1>
      <p className="text-[#6B7280] mb-6">Platform earnings and trust &amp; safety.</p>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6">
              <span className="w-10 h-10 rounded-2xl flex items-center justify-center mb-3" style={{ background: s.bg, color: s.color }}>
                <Icon size={20} />
              </span>
              <p className="text-xs font-semibold uppercase text-[#9CA3AF]">{s.label}</p>
              <p className="font-heading font-black text-3xl text-[#1A1D20] mt-1">{s.value}</p>
              <p className="text-xs text-[#9CA3AF] mt-0.5">{s.sub}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Reports & moderation */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-extrabold text-xl text-[#1A1D20] flex items-center gap-2">
          <Flag size={18} className="text-[#B23B3B]" /> Reports
          {filter !== "resolved" && openCount > 0 && (
            <span className="text-xs font-bold bg-[#FBE9E9] text-[#B23B3B] rounded-full px-2.5 py-0.5">{openCount} open</span>
          )}
        </h2>
        <div className="flex gap-1.5">
          {["open", "resolved", "all"].map((f) => (
            <button key={f} data-testid={`report-filter-${f}`} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 capitalize transition-colors ${filter === f ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-[#6B7280]"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {loadingReports ? (
        <div className="flex justify-center py-12 text-[#6B7280]"><Loader2 className="animate-spin" /></div>
      ) : reports.length === 0 ? (
        <div data-testid="reports-empty" className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 text-center text-[#9CA3AF]">
          No {filter === "all" ? "" : filter} reports. The community is behaving! 🎉
        </div>
      ) : (
        <div className="space-y-3" data-testid="reports-list">
          {reports.map((r) => (
            <div key={r.id} data-testid={`report-${r.id}`} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-[#EEF1F4] text-[#4A5361]">{r.target_type}</span>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${REASON_STYLE}`}>{r.reason || "No reason"}</span>
                {r.status === "resolved" && <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#E7F6F0] text-[#2F8F6E]">Resolved</span>}
                {r.upheld && <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#FBE9E9] text-[#B23B3B]">Upheld</span>}
                {r.target_hidden && <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#FFF7ED] text-[#9A6212]">Auto-hidden</span>}
                {r.target_owner_banned && <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#1A1D20] text-white">User banned</span>}
                <span className="text-xs text-[#9CA3AF] ml-auto">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
              </div>
              <p className="font-bold text-[#1A1D20]">
                {r.target_exists ? (r.target_title || "(untitled)") : <span className="italic text-[#9CA3AF]">Content already removed</span>}
              </p>
              <p className="text-xs text-[#6B7280] mb-3">
                {r.target_owner_alias && <>Owner: <span className="font-semibold">{r.target_owner_alias}</span> · </>}
                Reported by {r.reporter_alias || "someone"}
              </p>
              <div className="flex flex-wrap gap-2">
                {r.target_exists && r.target_type !== "user" && (
                  <Button data-testid={`report-remove-${r.id}`} onClick={() => removeContent(r)} disabled={busyId === r.id}
                    size="sm" className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333] h-8">
                    <Trash2 size={14} className="mr-1.5" /> Remove content
                  </Button>
                )}
                {r.target_owner_id && !r.target_owner_banned && (
                  <Button data-testid={`report-ban-${r.id}`} onClick={() => banUser(r)} disabled={busyId === r.id}
                    size="sm" variant="outline" className="rounded-full font-bold border-2 border-[#1A1D20] text-[#1A1D20] hover:bg-[#1A1D20] hover:text-white h-8">
                    <UserX size={14} className="mr-1.5" /> Ban user
                  </Button>
                )}
                {r.target_owner_id && r.target_owner_banned && (
                  <Button data-testid={`report-unban-${r.id}`} onClick={() => unbanUser(r)} disabled={busyId === r.id}
                    size="sm" variant="outline" className="rounded-full font-bold border-2 border-gray-200 h-8">
                    <ShieldOff size={14} className="mr-1.5" /> Unban
                  </Button>
                )}
                {r.status === "open" && (
                  <Button data-testid={`report-dismiss-${r.id}`} onClick={() => dismiss(r)} disabled={busyId === r.id}
                    size="sm" variant="ghost" className="rounded-full font-semibold text-[#6B7280] h-8">
                    <Check size={14} className="mr-1.5" /> Dismiss
                  </Button>
                )}
                {busyId === r.id && <Loader2 className="animate-spin text-[#9CA3AF] self-center" size={16} />}
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-heading font-extrabold text-xl text-[#1A1D20] mb-3 mt-10">Recent transactions</h2>
      {data.recent.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 text-center text-[#9CA3AF]">No paid transactions yet.</div>
      ) : (
        <div className="space-y-2">
          {data.recent.map((t) => {
            const fee = t.type === "cancellation_fee";
            return (
              <div key={t.id} data-testid={`txn-${t.id}`} className="flex items-center justify-between bg-white rounded-2xl border border-gray-100 shadow-soft p-4">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${fee ? "bg-[#FBE9E9] text-[#B23B3B]" : "bg-[#E7F6F0] text-[#2F8F6E]"}`}>
                    {fee ? "Cancellation fee" : "Errand payment"}
                  </span>
                  <span className="text-xs text-[#9CA3AF]">{t.updated_at ? new Date(t.updated_at).toLocaleString() : ""}</span>
                </div>
                <span className="font-heading font-extrabold text-[#1A1D20]">${Number(t.amount).toFixed(2)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
